import React, { useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { Button } from '@awazevr/design-system.components.button'
import { Logo } from '@awazevr/design-system.components.logo'
import { Heading, Text } from '@awazevr/design-system.components.text'
import { Stack } from '@awazevr/design-system.components.stack'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { ButtonLinkStyled } from '../buttonLinkStyled/buttonLinkStyled'
import { ContentfulData } from '../../../utils/contentful'
import { ReservationContext } from '../../lib/third-party-reservation'
import {
  LanguageSelector,
  buildLanguageChangeUrl,
} from '../languageSelector/languageSelector'
import { LANGUAGE_OPTIONS } from '../languageSelector/locales'
import { ResponsiveModal } from '../responsiveModal/responsiveModal'
import { XsPropertyCard } from '../xsPropertyCard/xsPropertyCard'
import { useBookingImage } from './useBookingImage'
import { useConsentResolved } from '../../hooks/useConsentResolved'

type OnboardingModalProps = {
  reservationContext: ReservationContext
  labels: ContentfulData['labels']
  links: ContentfulData['links']
  brand: string
  locale: string
}

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  outline: none;
`

const LogoWrapper = styled.div`
  svg {
    height: 24px;
    width: auto;
  }
`

// The DS focus trap focuses the first tabbable element, which would be the
// language selector. Move focus to the title instead (after the trap has run).
const useFocusOnMount = (): ((element: HTMLElement | null) => void) => {
  const timer = React.useRef<ReturnType<typeof setTimeout>>()
  return useCallback((element: HTMLElement | null) => {
    clearTimeout(timer.current)
    if (element) {
      timer.current = setTimeout(
        () => element.focus({ preventScroll: true }),
        50,
      )
    }
  }, [])
}

// Shown to forwarded third-party guests on sign-up. It cannot be closed; the
// guest either creates an account (dismissed for this tab) or goes to log-in.
export const OnboardingModal = ({
  reservationContext,
  labels,
  links,
  brand,
  locale,
}: OnboardingModalProps): React.ReactElement => {
  const router = useRouter()
  // Closed only for this page view, so the guest can reach the form. Nothing is
  // persisted: the modal returns on reload or revisit until they have created
  // an account or logged in.
  const [isOpen, setIsOpen] = useState(true)
  const titleRef = useFocusOnMount()
  const imageUrl = useBookingImage(reservationContext.houseNumber)
  // The DS focus trap blocks clicks outside the modal, so it must not open over
  // the cookie-consent banner or the guest cannot give consent.
  const consentResolved = useConsentResolved()

  // A full navigation reruns getServerSideProps so the Contentful copy loads
  // in the new locale. The dismissal flag is deliberately left alone.
  const handleLanguageChange = (newLocale: string) => {
    window.location.assign(
      buildLanguageChangeUrl({
        basePath: router.basePath,
        locale: newLocale,
        search: window.location.search,
      }),
    )
  }

  return (
    <ResponsiveModal isOpen={isOpen && consentResolved} disableClose>
      <Stack spacing={24} data-testid="onboarding-modal">
        <LanguageSelector
          options={LANGUAGE_OPTIONS}
          currentLocale={locale}
          onSelect={handleLanguageChange}
          label={labels['label_language']}
          leading={
            <LogoWrapper>
              <Logo brand={brand as Brand} aria-label={brand} />
            </LogoWrapper>
          }
        />
        <TitleBlock ref={titleRef} tabIndex={-1}>
          <Heading as="h2" sizes={200}>
            {labels['label_onboarding_title'] ??
              'Your booking is now on our new website'}
          </Heading>
          <Text sizes={200}>
            {labels['label_onboarding_body'] ??
              'Create a free account to view your booking. It takes less than a minute.'}
          </Text>
        </TitleBlock>
        <XsPropertyCard
          imageUrl={imageUrl}
          title={`${labels['label_booking_id'] ?? 'Booking ID'} ${reservationContext.reservationNumber}`}
          subtitle={
            labels['label_booked_via_third_party'] ?? 'Booked via a 3rd party'
          }
        />
        <Button
          text={labels['button_create_account'] ?? 'Create account'}
          colourScheme="brand-primary"
          onClick={() => setIsOpen(false)}
          data-testid="onboarding-create-account-button"
        />
        <Stack align="center">
          <Text sizes={200} as="span">
            {labels['label_have_account'] ?? 'Already have an account?'}{' '}
            <ButtonLinkStyled
              text={links['link_login']?.title ?? 'Log in'}
              type="button"
              onClick={() => router.push('/login')}
              data-testid="onboarding-login-button"
              colourScheme="brand-secondary"
              variant="text"
            />
          </Text>
        </Stack>
      </Stack>
    </ResponsiveModal>
  )
}
